/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  settingsApi,
  getSettingsCached,
  invalidateSettingsCache,
  BRANDING_SETTINGS_CHANGED_EVENT,
  type BrandingSettingsChangedDetail,
} from '../services/settingsApi'
import { logger } from '../utils/logger'
import { useAuth } from './AuthContext'
import { config } from '../config'
import { useSupportedLanguages, useDefaultLanguage } from './UiCustomizationContext'

type Language = 'en' | 'zh' | 'ja' | 'de' | 'fr' | 'ar'

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  loading: boolean
  canOverrideLanguage: boolean
  lockedBy: 'none' | 'platform' | 'tenant'
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

// Storage key from centralized config
const LANGUAGE_STORAGE_KEY = config.storage.language

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation()
  const [language, setLanguageState] = useState<Language>('en')
  const [loading, setLoading] = useState(true)
  const [canOverrideLanguage, setCanOverrideLanguage] = useState(true)
  const [lockedBy, setLockedBy] = useState<'none' | 'platform' | 'tenant'>('none')
  const { isAuthenticated, isCheckingAuth } = useAuth()
  const supportedLanguages = useSupportedLanguages()
  const defaultLanguage = useDefaultLanguage()

  // Load language from backend on mount
  useEffect(() => {
    // Wait for auth check to complete before making API calls
    if (isCheckingAuth) {
      return
    }

    const loadLanguage = async () => {
      try {
        // Only load from API if authenticated
        if (isAuthenticated) {
          try {
            const settings = await getSettingsCached()
            const savedLang = (settings.effective_language || settings.language || defaultLanguage || 'en') as Language
            setLanguageState(savedLang)
            setCanOverrideLanguage(settings.can_override_language !== false)
            setLockedBy(settings.locked_by || 'none')
            await i18n.changeLanguage(savedLang)
            if (settings.can_override_language !== false) {
              localStorage.setItem(LANGUAGE_STORAGE_KEY, savedLang)
            } else {
              localStorage.removeItem(LANGUAGE_STORAGE_KEY)
            }
            setLoading(false)
            return
          } catch (error) {
            // Silently fail - we have fallback
            logger.debug('Failed to load language from API, using localStorage', { error })
          }
        }

        // Fallback to localStorage, customer defaultLanguage, or browser language
        const storedLang = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language | null
        const browserLang = i18n.language.split('-')[0] as Language
        const allowedLangs = supportedLanguages.length > 0 ? supportedLanguages : ['en', 'zh', 'ja', 'de', 'fr', 'ar']
        
        // Priority: 1. User's stored preference, 2. Platform YAML default, 3. Browser language, 4. English fallback
        const defaultLang =
          storedLang || 
          (defaultLanguage as Language) || 
          (allowedLangs.includes(browserLang) ? browserLang : 'en')
          
        setLanguageState(defaultLang)
        await i18n.changeLanguage(defaultLang)
      } catch (error) {
        logger.error('Failed to load language', error)
        // Final fallback
        setLanguageState('en')
        await i18n.changeLanguage('en')
      } finally {
        setLoading(false)
      }
    }

    loadLanguage()
  }, [i18n, isAuthenticated, isCheckingAuth, supportedLanguages, defaultLanguage])

  // Re-sync when platform/tenant admin updates branding policy
  useEffect(() => {
    if (!isAuthenticated || isCheckingAuth) return

    const resyncLanguage = async (preview?: BrandingSettingsChangedDetail) => {
      try {
        const settings = await getSettingsCached()
        const savedLang = (
          preview?.previewLanguage ??
          settings.effective_language ??
          settings.language ??
          defaultLanguage ??
          'en'
        ) as Language
        setLanguageState(savedLang)
        setCanOverrideLanguage(settings.can_override_language !== false)
        setLockedBy(settings.locked_by || 'none')
        await i18n.changeLanguage(savedLang)
        if (settings.can_override_language !== false) {
          localStorage.setItem(LANGUAGE_STORAGE_KEY, savedLang)
        } else {
          localStorage.removeItem(LANGUAGE_STORAGE_KEY)
        }
      } catch (error) {
        logger.debug('Failed to re-sync language after branding change', { error })
      }
    }

    const onBrandingChanged = (event: Event) => {
      const detail = (event as CustomEvent<BrandingSettingsChangedDetail | undefined>).detail
      void resyncLanguage(detail)
    }
    window.addEventListener(BRANDING_SETTINGS_CHANGED_EVENT, onBrandingChanged)
    return () => window.removeEventListener(BRANDING_SETTINGS_CHANGED_EVENT, onBrandingChanged)
  }, [i18n, isAuthenticated, isCheckingAuth, defaultLanguage])

  const setLanguage = useCallback(
    async (lang: Language) => {
      if (!canOverrideLanguage) {
        logger.warn('Language is locked by administrator')
        return
      }
      try {
        // Update backend
        await settingsApi.updateSettings({ language: lang })
        invalidateSettingsCache()
        // Update i18n
        await i18n.changeLanguage(lang)
        // Update state
        setLanguageState(lang)
        // Update localStorage
        localStorage.setItem(LANGUAGE_STORAGE_KEY, lang)
      } catch (error) {
        logger.error('Failed to update language', error)
        // Still update locally even if backend fails
        await i18n.changeLanguage(lang)
        setLanguageState(lang)
        localStorage.setItem(LANGUAGE_STORAGE_KEY, lang)
      }
    },
    [canOverrideLanguage, i18n]
  )

  const value = useMemo(
    () => ({ language, setLanguage, loading, canOverrideLanguage, lockedBy }),
    [language, setLanguage, loading, canOverrideLanguage, lockedBy]
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider')
  }
  return context
}
